import React, { useState, useEffect } from 'react';
import { Cookie, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { trackPageView } from '../lib/analytics';

export function CookieBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const consent = localStorage.getItem('flashweb_cookie_consent');
      if (!consent) {
        const timer = setTimeout(() => setVisible(true), 1200);
        return () => clearTimeout(timer);
      }
    } catch (e) {}
  }, []);

  const updateConsent = (status: 'granted' | 'denied') => {
    try {
      localStorage.setItem('flashweb_cookie_consent', status);
      window.dispatchEvent(new CustomEvent('flashweb_cookie_consent_change', { detail: status }));
    } catch (e) {}

    if (typeof window !== 'undefined') {
      // Refusing consent must NOT load the tag manager: doing so defeats the
      // purpose of the banner and still shipped the full GTM bundle.
      if (status === 'granted' && (window as any).loadGTM) {
        (window as any).loadGTM();
      }
      if ((window as any).gtag) {
        (window as any).gtag('consent', 'update', {
          ad_storage: status,
          ad_user_data: status,
          ad_personalization: status,
          analytics_storage: status,
        });
      }
      if (status === 'granted') {
        trackPageView(window.location.pathname + window.location.search);
      }
    }

    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:left-6 sm:right-auto sm:bottom-6 sm:max-w-md z-[70] pointer-events-auto transition-all duration-300 ease-out animate-in fade-in slide-in-from-bottom-6"
    >
          <div className="relative rounded-2xl bg-white/95 dark:bg-[#0f1422]/95 backdrop-blur-xl border border-slate-200/90 dark:border-white/10 p-5 shadow-2xl shadow-black/10 dark:shadow-black/60 ring-1 ring-black/5 dark:ring-white/10">
            <button
              onClick={() => updateConsent('denied')}
              className="absolute top-3 right-3 p-1 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-white transition-colors cursor-pointer"
              aria-label={t('common.close', 'Fermer')}
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex items-start gap-3.5 pr-6">
              <div className="w-9 h-9 rounded-xl bg-primary text-primary-foreground flex items-center justify-center shrink-0 mt-0.5 shadow-xs">
                <Cookie className="w-5 h-5" />
              </div>
              <div className="space-y-1">
                <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                  {t('cookies.title', 'Respect de votre vie privée')}
                </h3>
                <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                  {t('cookies.desc', 'Nous utilisons des cookies pour mesurer l\'audience, garantir la sécurité et diffuser des annonces pertinentes.')}{' '}
                  <Link
                    to="/politique-confidentialite"
                    aria-label={t('cookies.learn_more_aria', 'En savoir plus sur notre politique de confidentialité')}
                    className="text-primary-ink hover:underline font-medium inline-block"
                  >
                    {t('cookies.learn_more', 'En savoir plus')}
                  </Link>
                </p>
              </div>
            </div>

            <div className="mt-4 pt-3 border-t border-slate-100 dark:border-white/10 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => updateConsent('denied')}
                className="px-3.5 py-1.5 rounded-xl text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5 transition-colors cursor-pointer"
              >
                {t('cookies.refuse', 'Continuer sans accepter')}
              </button>
              <button
                type="button"
                onClick={() => updateConsent('granted')}
                className="px-4 py-1.5 rounded-xl text-xs font-bold text-white bg-slate-950 hover:bg-slate-800 dark:bg-white dark:text-slate-950 shadow-md shadow-slate-950/25 transition-all hover:scale-[1.02] active:scale-[0.98] cursor-pointer"
              >
                {t('cookies.accept', 'Tout accepter')}
              </button>
            </div>
          </div>
        </div>
  );
}
